import { FolderSearch, Loader2, Plus, Search } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { DeleteCampaignDialog } from "@/components/campaigns/DeleteCampaignDialog";
import { EditCampaignDialog } from "@/components/campaigns/EditCampaignDialog";
import { ProfileBadge } from "@/components/campaigns/ProfileBadge";
import { RowActionsMenu } from "@/components/data-table/row-actions-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

interface Campaign {
  id: string;
  name: string;
  goal: string | null;
  status: string;
  locationCount: number;
  profileId: string | null;
  profileName: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CampaignPanelProps {
  activeCampaignId: string | null;
  onSelectCampaign: (id: string) => void;
  refreshKey: number;
  onNewCampaign: () => void;
}

export function CampaignPanel({
  activeCampaignId,
  onSelectCampaign,
  refreshKey,
  onNewCampaign,
}: CampaignPanelProps) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);
  const searchRef = useRef(search);
  const autoSelectedRef = useRef(false);
  // Populated when a row's Delete action is chosen. scoredCount is not in the
  // list payload, so it is fetched on demand — the dialog uses it to warn that
  // scored locations will be destroyed, not just raw ones.
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
    locationCount: number;
    scoredCount: number;
  } | null>(null);
  // Edit only needs the fields the form binds to, all of which the list already
  // has — no extra fetch, unlike the delete dialog's scored count.
  const [editTarget, setEditTarget] = useState<{
    id: string;
    name: string;
    goal: string | null;
  } | null>(null);

  const fetchCampaigns = useCallback(async (query: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (query) params.set("search", query);
      const res = await fetch(`/api/campaigns?${params}`);
      if (!res.ok) throw new Error("Failed to fetch");
      const json = await res.json();
      setCampaigns(json.data ?? []);
    } catch {
      // Silent fail, campaigns will be empty
    } finally {
      setLoading(false);
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshKey intentionally triggers re-fetch
  useEffect(() => {
    fetchCampaigns(searchRef.current);
  }, [fetchCampaigns, refreshKey]);

  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  // Entering the Campaigns section with nothing selected → auto-open the first
  // campaign that has locations, so results show immediately instead of an
  // empty "select a campaign" state. Fires once until a campaign is active.
  useEffect(() => {
    if (activeCampaignId) {
      autoSelectedRef.current = false;
      return;
    }
    if (loading || autoSelectedRef.current || search) return;
    const first = campaigns.find((c) => c.locationCount > 0);
    if (first) {
      autoSelectedRef.current = true;
      onSelectCampaign(first.id);
    }
  }, [activeCampaignId, loading, search, campaigns, onSelectCampaign]);

  const handleRequestDelete = useCallback(async (campaign: Campaign) => {
    let scoredCount = 0;
    try {
      const res = await fetch(`/api/campaigns/${campaign.id}/scoring-status`);
      if (res.ok) scoredCount = (await res.json()).scored ?? 0;
    } catch {
      // Non-fatal: fall back to 0 so the dialog still opens. It then describes
      // the delete in terms of locationCount, which is accurate but omits the
      // stronger scored-locations warning.
    }
    setDeleteTarget({
      id: campaign.id,
      name: campaign.name,
      locationCount: campaign.locationCount,
      scoredCount,
    });
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchCampaigns(value);
      }, 300);
    },
    [fetchCampaigns],
  );

  return (
    <div className="hidden h-full w-80 shrink-0 flex-col overflow-hidden border-r border-border bg-muted/30 md:flex">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold">Campaigns</h2>
        <Button size="sm" variant="default" className="h-7 gap-1 text-xs" onClick={onNewCampaign}>
          <Plus className="h-3.5 w-3.5" />
          New
        </Button>
      </div>

      {/* Search */}
      <div className="px-3 py-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search campaigns..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-8 pl-8 text-sm"
            autoComplete="off"
          />
        </div>
      </div>

      {/* Campaign list (scrollable) */}
      <div className="flex-1 overflow-y-auto px-2 py-1">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && campaigns.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-2 px-4 py-8 text-center">
            <FolderSearch className="h-6 w-6 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">
              {search ? "No campaigns match your search." : "No campaigns yet."}
            </p>
          </div>
        )}

        {!loading &&
          campaigns.map((campaign) => (
            // The card owns the border/background; the button inside owns
            // selection. The kebab has to live outside that button — nesting a
            // button in a button is invalid, and its click would select the row.
            <div
              key={campaign.id}
              className={cn(
                "group mb-1.5 flex items-start rounded-lg border transition-colors",
                campaign.id === activeCampaignId
                  ? "border-primary/30 bg-primary/5"
                  : "border-border hover:border-primary/30 hover:bg-muted/50",
              )}
            >
              <button
                type="button"
                onClick={() => {
                  if (campaign.locationCount === 0) {
                    onNewCampaign();
                  } else {
                    onSelectCampaign(campaign.id);
                  }
                }}
                className="flex min-w-0 flex-1 flex-col p-3 text-left"
              >
                {/* Row 1: Name (1 line, truncated) + location count badge */}
                <div className="flex items-center gap-1.5">
                  <span className="flex-1 truncate text-sm font-medium">{campaign.name}</span>
                  {campaign.locationCount === 0 ? (
                    <Badge variant="secondary" className="shrink-0 text-[10px]">
                      Draft
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="shrink-0 text-[10px] tabular-nums">
                      {campaign.locationCount}
                    </Badge>
                  )}
                </div>

                {/* Row 2-3: Goal (always 2 lines — clamped or empty space) */}
                <p className="mt-1 line-clamp-2 min-h-8 text-xs text-muted-foreground">
                  {campaign.goal ?? " "}
                </p>

                {/* Row 4: Profile badge (left) + created date (right) */}
                <div className="mt-1.5 flex items-center justify-between">
                  <div>
                    {campaign.profileName ? (
                      <ProfileBadge name={campaign.profileName} />
                    ) : (
                      <span className="text-[10px] italic text-muted-foreground/50">
                        No profile
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] tabular-nums text-muted-foreground">
                    {formatDate(campaign.createdAt)}
                  </span>
                </div>
              </button>

              {/* Kept subtle until the row is hovered or the trigger is focused,
                so the list still reads as a list. focus-within keeps it
                reachable by keyboard. */}
              <div className="shrink-0 pr-1 pt-2 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                <RowActionsMenu
                  label={`Actions for ${campaign.name}`}
                  actions={[
                    {
                      label: "Edit campaign",
                      onClick: () => {
                        setEditTarget({
                          id: campaign.id,
                          name: campaign.name,
                          goal: campaign.goal,
                        });
                      },
                    },
                  ]}
                  destructiveActions={[
                    {
                      label: "Delete campaign",
                      onClick: () => {
                        void handleRequestDelete(campaign);
                      },
                    },
                  ]}
                />
              </div>
            </div>
          ))}
      </div>

      {editTarget && (
        <EditCampaignDialog
          campaign={editTarget}
          open
          onOpenChange={(open) => {
            if (!open) setEditTarget(null);
          }}
          onSaved={() => {
            setEditTarget(null);
            fetchCampaigns(searchRef.current);
          }}
        />
      )}

      {deleteTarget && (
        <DeleteCampaignDialog
          campaign={deleteTarget}
          open
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null);
          }}
          // Only redirect when the campaign being deleted is the one on screen;
          // its view is gone. Deleting any other row should leave the user in
          // the campaigns section.
          navigateAfterDelete={deleteTarget.id === activeCampaignId}
          onDeleted={() => {
            setDeleteTarget(null);
            fetchCampaigns(searchRef.current);
          }}
        />
      )}
    </div>
  );
}
